import { useState, useEffect, useRef, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { api } from '@/services/api-class';

const CUSTOM_OPTION_ID = -1;

export interface StatusUpdateModalProps {
  open: boolean;
  theme?: string;
  officers?: { id: number; name: string }[];
  onUpdate: (statusText: string) => void;
  onUpdateOfficer?: (statusText: string, targetPersonId: number) => void;
  onClear: () => void;
  onClearOfficer?: (targetPersonId: number) => void;
  onCancel: () => void;
}

export function StatusUpdateModal({
  open,
  theme,
  officers,
  onUpdate,
  onUpdateOfficer,
  onClear,
  onClearOfficer,
  onCancel,
}: StatusUpdateModalProps) {
  const [customText, setCustomText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [options, setOptions] = useState<any[]>([]);
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [isCustom, setIsCustom] = useState(false);
  const [selectedOfficerId, setSelectedOfficerId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasOptions = options.length > 0;
  const hasOfficers = Array.isArray(officers) && officers.length > 0;

  // Build dropdown items: predefined options + "Custom..." at end
  const dropdownItems = hasOptions
    ? [...options, { id: CUSTOM_OPTION_ID, name: 'Custom...' }]
    : [];

  // Load options and reset form when modal opens
  useEffect(() => {
    if (open) {
      setCustomText('');
      setSelectedOptionId(null);
      setSelectedOfficerId(null);
      setIsCustom(false);
      setErrorMessage('');
      api.listStatusUpdateOptions((result: any) => {
        const opts = (result.statusUpdateOptions || [])
          .filter((o: any) => o.isEnabled)
          .sort((a: any, b: any) => a.listIdx - b.listIdx);
        setOptions(opts);
      }, () => {
        setOptions([]);
      });
    }
  }, [open]);

  // Auto-focus custom input when it appears
  useEffect(() => {
    if (isCustom) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isCustom]);

  // Auto-focus text input when no options (free-text only mode)
  useEffect(() => {
    if (open && !hasOptions) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [open, hasOptions]);

  const handleOptionChange = useCallback((value: string | number | null) => {
    setErrorMessage('');
    if (value === CUSTOM_OPTION_ID) {
      setSelectedOptionId(null);
      setIsCustom(true);
      setCustomText('');
    } else {
      setSelectedOptionId(value as number | null);
      setIsCustom(false);
      setCustomText('');
    }
  }, []);

  const handleSave = useCallback(() => {
    let statusText: string;
    if (hasOptions && !isCustom) {
      // Dropdown mode — must have a selection
      if (selectedOptionId == null) {
        setErrorMessage('Please select a status.');
        return;
      }
      const option = options.find((o: any) => o.id === selectedOptionId);
      if (!option) return;
      statusText = option.name;
    } else {
      // Custom/free-text mode
      if (customText.trim() === '') {
        setErrorMessage('Please enter a status.');
        return;
      }
      statusText = customText.trim();
    }

    if (selectedOfficerId != null && onUpdateOfficer) {
      onUpdateOfficer(statusText, selectedOfficerId);
    } else {
      onUpdate(statusText);
    }
  }, [hasOptions, isCustom, selectedOptionId, selectedOfficerId, options, customText, onUpdate, onUpdateOfficer]);

  const handleKeydown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSave();
      }
    },
    [handleSave]
  );

  return (
    <BaseFormModal
      open={open}
      title="Status Update"
      saveText="Update"
      cancelText="Cancel"
      saveButtonClass="kstc-btn--primary"
      theme={theme}
      onSave={handleSave}
      onCancel={onCancel}
    >
      {hasOfficers && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 13, opacity: 0.75, marginBottom: 4 }}>Updating status for:</label>
          <Dropdownlist
            value={selectedOfficerId}
            items={officers!}
            labelKey="name"
            valueKey="id"
            placeholder="Myself"
            sortItems={true}
            searchable={true}
            clearable={true}
            onChange={(val: any) => setSelectedOfficerId(val)}
          />
        </div>
      )}
      {hasOptions && (
        <Dropdownlist
          value={isCustom ? CUSTOM_OPTION_ID : selectedOptionId}
          items={dropdownItems}
          labelKey="name"
          valueKey="id"
          placeholder="Select a status..."
          sortItems={false}
          searchable={false}
          clearable={false}
          onChange={handleOptionChange}
        />
      )}
      {(isCustom || !hasOptions) && (
        <div style={{ marginTop: hasOptions ? 12 : 0 }}>
          <input
            ref={inputRef}
            type="text"
            className="form-control"
            value={customText}
            onChange={(e) => { setCustomText(e.target.value); setErrorMessage(''); }}
            placeholder="Enter status..."
            onKeyDown={handleKeydown}
          />
        </div>
      )}
      {errorMessage && (
        <p className="text-danger" style={{ marginTop: 8 }}>
          {errorMessage}
        </p>
      )}
      <div style={{ marginTop: 12, textAlign: 'center' }}>
        <button
          type="button"
          // Buttons don't inherit color from the dialog by default, so ask for it
          // explicitly and mute with opacity instead of a hardcoded gray.
          style={{ background: 'none', border: 'none', color: 'inherit', opacity: 0.75, cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}
          onClick={() => {
            if (selectedOfficerId != null && onClearOfficer) {
              onClearOfficer(selectedOfficerId);
            } else {
              onClear();
            }
          }}
        >
          Clear Status
        </button>
      </div>
    </BaseFormModal>
  );
}

(window as any).StatusUpdateModalReact = StatusUpdateModal;
